import { Alert, Button, Input, Modal, Radio, Result, Select, Space, Steps, Table, Upload, message } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { ColumnsType } from 'antd/lib/table';
import { SelectValue } from 'antd/lib/select';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import UsersManagerStore from '../stores/UsersManagerStore';
import { BulkUserImportResponse, BulkUserRow } from '../types/BulkUserModels';
import { ProjectResult } from '../../common/types';

type Props = {
    store: UsersManagerStore;
};

export const DEFAULT_BULK_USERS_ROLE = 'user';

export const buildRowsWithEffectiveAssignments = (rows: BulkUserRow[], defaultRole: string): BulkUserRow[] =>
    rows.map(row => ({
        ...row,
        role: row.role || defaultRole
    }));

export const getDefaultBulkUsersProjectId = (
    projects: ProjectResult[],
    currentProjectId?: string
): string | undefined => currentProjectId || (projects.length === 1 ? projects[0].id : undefined);

const BulkUsersDialog: React.FC<Props> = ({ store }) => {
    const [step, setStep] = React.useState(0);
    const [rows, setRows] = React.useState<BulkUserRow[]>([]);
    const [errors, setErrors] = React.useState<string[]>([]);
    const [projectId, setProjectId] = React.useState<string>();
    const [role, setRole] = React.useState<string>(DEFAULT_BULK_USERS_ROLE);
    const [importResponse, setImportResponse] = React.useState<BulkUserImportResponse>();
    const [isWorking, setIsWorking] = React.useState(false);
    const [exportMode, setExportMode] = React.useState<'all' | 'batch'>('all');
    const [selectedBatchId, setSelectedBatchId] = React.useState<string>();

    React.useEffect(() => {
        if (store.bulkUsersDialogVisible) {
            store.loadBulkUsersLookups().catch(err => {
                console.error(err);
                message.error('Could not load bulk user data.');
            });
        }
    }, [store, store.bulkUsersDialogVisible]);

    React.useEffect(() => {
        if (!store.bulkUsersDialogVisible) {
            setStep(0);
            setRows([]);
            setErrors([]);
            setProjectId(undefined);
            setRole(DEFAULT_BULK_USERS_ROLE);
            setImportResponse(undefined);
            setExportMode('all');
            setSelectedBatchId(undefined);
        }
    }, [store.bulkUsersDialogVisible]);

    React.useEffect(() => {
        if (
            store.bulkUsersDialogVisible &&
            store.bulkUsersDialogMode === 'import' &&
            store.bulkUsersProjects.length === 1
        ) {
            setProjectId(currentProjectId => getDefaultBulkUsersProjectId(store.bulkUsersProjects, currentProjectId));
        }
    }, [store.bulkUsersDialogMode, store.bulkUsersDialogVisible, store.bulkUsersProjects]);

    const hasRowErrors = rows.some(row => row.errors && row.errors.length > 0);
    const selectedProject = store.bulkUsersProjects.find(project => project.id === projectId);
    const selectString = (value: SelectValue) => value?.toString();

    const handleUpload = async (file: RcFile) => {
        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            message.error('Please upload an .xlsx file.');
            return false;
        }

        setIsWorking(true);
        try {
            const result = await store.validateBulkUsersFile(file);
            if (result) {
                setRows(result.rows);
                setErrors(result.errors || []);
            }
        } finally {
            setIsWorking(false);
        }

        return false;
    };

    const updateRow = (rowNumber: number, field: keyof BulkUserRow, value: string) => {
        setRows(current => current.map(row => (row.rowNumber === rowNumber ? { ...row, [field]: value } : row)));
    };

    const revalidateRows = async () => {
        setIsWorking(true);
        try {
            const result = await store.validateBulkUsersRows(rows);
            setRows(result.rows);
            setErrors(result.errors || []);
        } finally {
            setIsWorking(false);
        }
    };

    const importUsers = async () => {
        if (!projectId || !role) {
            return;
        }
        setIsWorking(true);
        try {
            const result = await store.importBulkUsers({
                rows: buildRowsWithEffectiveAssignments(rows, role),
                projectId,
                projectName: selectedProject?.name || projectId,
                role
            });
            if (result) {
                setImportResponse(result);
            }
        } finally {
            setIsWorking(false);
        }
    };

    const exportCredentials = async () => {
        setIsWorking(true);
        try {
            await store.exportBulkUsers(exportMode, exportMode === 'batch' ? selectedBatchId : undefined);
        } finally {
            setIsWorking(false);
        }
    };

    const columns: ColumnsType<BulkUserRow> = [
        { title: 'Row', dataIndex: 'rowNumber', width: 70 },
        {
            title: 'Full name',
            dataIndex: 'fullName',
            render: (value: string, row) => (
                <Input value={value} onChange={e => updateRow(row.rowNumber, 'fullName', e.target.value)} />
            )
        },
        {
            title: 'Email',
            dataIndex: 'email',
            render: (value: string, row) => (
                <Input value={value} onChange={e => updateRow(row.rowNumber, 'email', e.target.value)} />
            )
        },
        {
            title: 'Username',
            dataIndex: 'userName',
            render: (value: string, row) => (
                <Input value={value} onChange={e => updateRow(row.rowNumber, 'userName', e.target.value)} />
            )
        },
        {
            title: 'Status',
            dataIndex: 'errors',
            render: (rowErrors: string[]) => (rowErrors && rowErrors.length ? rowErrors.join(' ') : 'Ready')
        }
    ];

    const assignmentColumns: ColumnsType<BulkUserRow> = [
        {
            title: 'User',
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <span>{row.fullName}</span>
                    <span className="alpha-secondary-text">{row.userName}</span>
                </Space>
            )
        },
        { title: 'Email', dataIndex: 'email' },
        {
            title: 'Role',
            width: 220,
            render: (_, row) => (
                <Select
                    style={{ width: '100%' }}
                    placeholder={role ? `Default: ${role}` : 'Select the default role first'}
                    value={row.role || role}
                    disabled={!role}
                    onChange={value => updateRow(row.rowNumber, 'role', selectString(value) || '')}
                    options={store.rolesWithLabels}
                />
            )
        }
    ];

    const renderImportStep = () => {
        if (step === 0) {
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Upload.Dragger
                        accept=".xlsx"
                        beforeUpload={handleUpload}
                        showUploadList={false}
                        disabled={isWorking}
                    >
                        <p className="ant-upload-drag-icon">
                            <i className="alpha-icon xl import-icon" />
                        </p>
                        <p className="ant-upload-text">Upload the completed user template</p>
                        <p className="ant-upload-hint">Expected columns: Full name, Email</p>
                    </Upload.Dragger>
                    {errors.map(error => (
                        <Alert key={error} type="error" message={error} showIcon />
                    ))}
                    {rows.length > 0 && (
                        <>
                            <Table
                                rowKey={row => row.rowNumber}
                                className="alpha-portal-table"
                                columns={columns}
                                dataSource={rows}
                                pagination={false}
                                size="small"
                                rowClassName={row => (row.errors && row.errors.length ? 'ant-table-row-error' : '')}
                            />
                        </>
                    )}
                </Space>
            );
        }

        if (step === 1) {
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Select
                        size="large"
                        style={{ width: '100%' }}
                        placeholder="Select project"
                        value={projectId}
                        onChange={value => setProjectId(selectString(value))}
                        options={store.bulkUsersProjects.map(project => ({ value: project.id, label: project.name }))}
                    />
                    <Select
                        size="large"
                        style={{ width: '100%' }}
                        placeholder="Select default project role"
                        value={role}
                        onChange={value => setRole(selectString(value) || DEFAULT_BULK_USERS_ROLE)}
                        options={store.rolesWithLabels}
                    />
                    <Table
                        rowKey={row => row.rowNumber}
                        className="alpha-portal-table"
                        columns={assignmentColumns}
                        dataSource={rows}
                        pagination={false}
                        size="small"
                    />
                </Space>
            );
        }

        return importResponse ? (
            <Result
                status="success"
                title={`${importResponse.userCount} users imported`}
                subTitle={importResponse.batchLabel}
                extra={[
                    <Button
                        key="export"
                        type="primary"
                        onClick={() => store.downloadBulkCredentials(importResponse)}
                        data-id="bulk-users-export-immediate"
                    >
                        Export Credentials Sheet
                    </Button>
                ]}
            />
        ) : (
            <Alert
                type="info"
                showIcon
                message={`Ready to create ${rows.length} users`}
                description="Passwords will be available only from the credential sheet generated after this import."
            />
        );
    };

    const renderExport = () => (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Radio.Group value={exportMode} onChange={e => setExportMode(e.target.value)}>
                <Radio value="all">Export all users</Radio>
                <Radio value="batch">Export by batch</Radio>
            </Radio.Group>
            {exportMode === 'batch' && (
                <Select
                    size="large"
                    style={{ width: '100%' }}
                    placeholder="Select import batch"
                    value={selectedBatchId}
                    onChange={value => setSelectedBatchId(selectString(value))}
                    options={store.bulkUserBatches.map(batch => ({ value: batch.id, label: batch.label }))}
                />
            )}
            <Alert
                type="warning"
                showIcon
                message="Retrospective exports cannot include passwords."
                description="Password cells will contain Unavailable after initial export."
            />
        </Space>
    );

    const isExport = store.bulkUsersDialogMode === 'export';
    const canGoNext = step === 0 ? rows.length > 0 && !hasRowErrors && !errors.length : !!projectId && !!role;
    const canExport = exportMode === 'all' || !!selectedBatchId;
    const showRevalidateRows = !isExport && step === 0 && rows.length > 0;

    return (
        <Modal
            visible={store.bulkUsersDialogVisible}
            title={isExport ? 'Export Credentials Sheet' : 'Add Users in Bulk'}
            width={1000}
            centered
            destroyOnClose
            className="alpha-portal-dialog"
            onCancel={() => store.setBulkUsersDialogVisible(false)}
            footer={
                isExport
                    ? [
                          <Button key="cancel" className="light" onClick={() => store.setBulkUsersDialogVisible(false)}>
                              Cancel
                          </Button>,
                          <Button
                              key="export"
                              type="primary"
                              loading={isWorking}
                              disabled={!canExport}
                              onClick={exportCredentials}
                          >
                              Export
                          </Button>
                      ]
                    : [
                          showRevalidateRows ? (
                              <Button
                                  key="revalidate"
                                  style={{ float: 'left' }}
                                  onClick={revalidateRows}
                                  loading={isWorking}
                              >
                                  Revalidate rows
                              </Button>
                          ) : null,
                          <Button key="cancel" className="light" onClick={() => store.setBulkUsersDialogVisible(false)}>
                              {importResponse ? 'Close' : 'Cancel'}
                          </Button>,
                          step > 0 && !importResponse ? (
                              <Button key="back" onClick={() => setStep(step - 1)}>
                                  Back
                              </Button>
                          ) : null,
                          step < 2 ? (
                              <Button key="next" type="primary" disabled={!canGoNext} onClick={() => setStep(step + 1)}>
                                  Next
                              </Button>
                          ) : null,
                          step === 2 && !importResponse ? (
                              <Button key="import" type="primary" loading={isWorking} onClick={importUsers}>
                                  Create users
                              </Button>
                          ) : null
                      ]
            }
        >
            {isExport ? (
                renderExport()
            ) : (
                <>
                    <Steps current={step} style={{ marginBottom: 24 }}>
                        <Steps.Step title="Review & Validate" />
                        <Steps.Step title="Assign Project & Role" />
                        <Steps.Step title="Confirm & Export" />
                    </Steps>
                    {renderImportStep()}
                </>
            )}
        </Modal>
    );
};

export default observer(BulkUsersDialog);
